import React, { useState, useEffect } from 'react';
import moment from 'moment';
import PropTypes from 'prop-types';
import Button from '../button/Button';
import Separator from '../separator/Separator';
import NewDialog from '../newDialog/NewDialog';
import Dialog from '../dialog/Dialog';
import CancelModal from '../cancelModal/CancelModal';
import {
  isCurrentTimeAfterEvent,
  getDateLabel,
  isNowAfterTime,
  IsSelectedEventExpired,
  areAllCheckedSlotsAlreadyBooked,
  getArrayWithUniqueElements,
  getClashedBookedEntertainments,
  getRemovableEntertainmentDateLabel,
  getIsChecked,
  getSlotColor,
  redirectUserToSchedulePageWithReload,
  checkIsConfigNeedsToBeRefreshed,
  filterSlotsByBackstop,
  isSplash,
} from '../../utils';
import { virtualPageView } from '../../googleTagManager';
import '../entertainmentOptions/EntertainmentOptions.scss';
import GuestSelector from '../guestSelector/GuestSelector';
import DialogMessage from '../dialogMessage/DialogMessage';
import SwimmingOption from '../swimmingOption/SwimmingOption';
import WarningMessage from '../warningMessage/WarningMessage';

const getOptionDisabled = (
  checked,
  areSpacesAvailable,
  singleDate = '',
  duration = 0,
  isAlreadyBooked,
  isDisabledSplashForSelectedDay,
) => !checked
&& !isAlreadyBooked
  && (!areSpacesAvailable
    || isCurrentTimeAfterEvent(singleDate, duration)
    || isDisabledSplashForSelectedDay
  );

const getShouldDisableSwimming = (
  arrivalDate,
  poolRestrictionTime,
  singleDate,
  allBookedAndCheckedPoolSlots,
  numOfPreBreakMaxAllowedPoolSlots,
  isAfterDailyOpeningTime,
  maxAllowedPoolSlotsOnSameDay,
  code,
  checked,
  allBookedAndCheckedPoolSlotsOnThisDay,
) => {
  const isOnBreak = isNowAfterTime(arrivalDate, poolRestrictionTime);
  const isToday = moment().isSame(singleDate, 'day');
  const hasReachedPreBreakMax = allBookedAndCheckedPoolSlots.length >= numOfPreBreakMaxAllowedPoolSlots;
  if (isOnBreak && hasReachedPreBreakMax && !isAfterDailyOpeningTime) return true;
  const isOptionInChecked = checked.filter(({ slotId }) => slotId === code).length > 0;
  if (isOnBreak && isToday && hasReachedPreBreakMax && isAfterDailyOpeningTime
    && maxAllowedPoolSlotsOnSameDay <= allBookedAndCheckedPoolSlotsOnThisDay.length && !isOptionInChecked) return true;
  if (isOnBreak && !isToday && hasReachedPreBreakMax) return true;
  return false;
};

const handleCancel = (setChecked, removeAddon, bookedSlotsOfThisEventWithoutPast, setShowCancelDialog, checked) => {
  if (checked.length > 1) {
    setShowCancelDialog(true);
    return;
  }
  if (checked.length > 0 && bookedSlotsOfThisEventWithoutPast.length === 0) setChecked([]);
  if (checked.length > 0 && bookedSlotsOfThisEventWithoutPast.length > 0) {
    setChecked([]);
    removeAddon();
  }
};

const getIsAllowedToCheckMorePoolSlots = (
  arrivalDate,
  isAllowedToBookMorePoolSlots,
  maxAllowedPoolSlots,
  maxAllowedPoolSlotsOnSameDay,
  allBookedAndCheckedSlots,
  checked,
  poolRestrictionTime,
  numOfPreBreakMaxAllowedPoolSlots,
) => {
  const hasReachedPreBreakMax = allBookedAndCheckedSlots.length >= numOfPreBreakMaxAllowedPoolSlots;
  if (!isNowAfterTime(arrivalDate, poolRestrictionTime)) return isAllowedToBookMorePoolSlots && maxAllowedPoolSlots > allBookedAndCheckedSlots.length;
  return isAllowedToBookMorePoolSlots
    && maxAllowedPoolSlots > allBookedAndCheckedSlots.length
    && ((hasReachedPreBreakMax && maxAllowedPoolSlotsOnSameDay > checked.length) || !hasReachedPreBreakMax);
};

const onOptionClick = (
  setChecked,
  disabled,
  code,
  setPoolDialogData,
  checked,
  isOnlyOnePoolSlotAllowed = false,
  setGuestSelectorDialogData,
  numOfGuests,
  clashedEvents = [],
  setClashedEvents,
  setOverlapsDialogData,
  preventOverlapMandatory,
  isAllowedToBookMorePoolSlots = false,
  isAllowedToReplaceCheckedSlot = false,
) => {
  if (disabled || checked.filter(({ slotId }) => slotId === code).length > 0) return;
  const isClashedEvents = clashedEvents.length > 0;
  const isMandatoryOverlapAndClashed = preventOverlapMandatory && isClashedEvents;
  const isNotMandatoryOverlapOrNotClashed = !preventOverlapMandatory || !isClashedEvents;
  const isNotMandatoryOverlapButClashed = !preventOverlapMandatory && isClashedEvents;

  if (isClashedEvents) setClashedEvents(clashedEvents);
  if (!isAllowedToBookMorePoolSlots && !isAllowedToReplaceCheckedSlot) {
    setPoolDialogData({ show: true, relatedSlotId: code });
    virtualPageView('plan my day', 'book swimming', '/ga-virtual/plan-my-day/book-swimming/pre-arrival-limit-notice');
  }
  if (!isAllowedToBookMorePoolSlots && !isOnlyOnePoolSlotAllowed) return;
  if (isMandatoryOverlapAndClashed) setOverlapsDialogData({ show: true, relatedSlotId: code });
  if (isNotMandatoryOverlapOrNotClashed && (isAllowedToBookMorePoolSlots || isAllowedToReplaceCheckedSlot) && numOfGuests > 1) {
    setGuestSelectorDialogData({ show: true, relatedSlotId: code });
    return;
  }
  if (isNotMandatoryOverlapButClashed && numOfGuests === 1) setOverlapsDialogData({ show: true, relatedSlotId: code });
  if (!isAllowedToBookMorePoolSlots && isAllowedToReplaceCheckedSlot) {
    setChecked([{
      slotId: code,
      guestNumber: 1,
    }]);
    return;
  }
  setChecked([...checked, {
    slotId: code,
    guestNumber: 1,
  }]);
};

const getPoolDialogMessage = (isOnlyOnePoolSlotAllowed, arrivalDate, clashedEventsForTheSelectedEvent, poolRestrictionTime) => {
  const isClashedEvent = clashedEventsForTheSelectedEvent && clashedEventsForTheSelectedEvent.length > 0;
  if (isNowAfterTime(arrivalDate, poolRestrictionTime)) {
    return [
      "We're limiting the number of session that can be booked, guaranteeing everyone access.",
      isClashedEvent
      && 'Please note that this new session overlaps with another booking that you already have.',
      isOnlyOnePoolSlotAllowed
        ? 'Choosing a new time will replace your existing swimming session:'
        : 'You have already booked the maximum number of sessions, please remove one of these before you can book an additional one.',
    ];
  }
  return [
    "We're limiting the number of session that can be booked before arrival, guaranteeing everyone access. During your stay, you will be able book additional sessions.",
    isClashedEvent
    && 'Please note that this new session overlaps with another booking that you already have.',
    isOnlyOnePoolSlotAllowed
      ? 'Choosing a new time will replace your existing pre-arrival swimming session:'
      : 'You have already booked the maximum number of pre-arrival sessions, please remove one of these before you can book an additional one.',
  ];
};

const onPositivePoolDialogButtonClick = (
  isOnlyOnePoolSlotAllowed,
  setShowDialog,
  setChecked,
  checked,
  setClashedEvents,
  setGuestSelectorDialogData,
  { relatedSlotId },
) => {
  setClashedEvents([]);
  setShowDialog({ show: false, relatedSlotId: null });
  if (isOnlyOnePoolSlotAllowed && checked.length > 1) setChecked(checked.slice(1));
  if (isOnlyOnePoolSlotAllowed) setGuestSelectorDialogData({ show: true, relatedSlotId });
};

const onNegativePoolDialogButtonClick = (setChecked, setShowDialog, checked, isOnlyOnePoolSlotAllowed, setClashedEvents) => {
  setClashedEvents([]);
  if (isOnlyOnePoolSlotAllowed) setChecked(checked.slice(0, -1));
  setShowDialog({ show: false, relatedSlotId: null });
};

const getIsOnlyOnePoolSlotAllowed = (
  arrivalDate,
  maxAllowedPoolSlots,
  maxAllowedPoolSlotsOnSameDay,
  poolRestrictionTime,
) => (isNowAfterTime(arrivalDate, poolRestrictionTime) ? maxAllowedPoolSlotsOnSameDay === 1 : maxAllowedPoolSlots === 1);

const onPositiveSavePoolDialogButtonClick = (
  setChecked,
  checked = [],
  setGuestSelectorDialogData,
  setClashedEvents,
  { relatedSlotId },
  isAllowedToBookMorePoolSlots,
  numberOfGuestsForSwimming = 1,
) => {
  setClashedEvents([]);
  const newSlot = { slotId: relatedSlotId, guestNumber: numberOfGuestsForSwimming };
  const updatedCheckedSlots = !isAllowedToBookMorePoolSlots ? [newSlot] : [...checked, newSlot];
  setChecked(updatedCheckedSlots);
  setGuestSelectorDialogData({ show: false, relatedSlotId: null });
};

const onNegativeSavePoolDialogButtonClick = (setGuestSelectorDialogData, setClashedEvents) => {
  setClashedEvents([]);
  setGuestSelectorDialogData({ show: false, relatedSlotId: null });
};

const getClashedDialogMessage = (clashedEventsForTheSelectedEvent, preventOverlapMandatory) => {
  const isOnlyOneClashedEvent = clashedEventsForTheSelectedEvent.length === 1;
  if (preventOverlapMandatory) {
    if (isOnlyOneClashedEvent) {
      const [{ name: clashedName, startTime }] = clashedEventsForTheSelectedEvent;
      return [`This overlaps with booking ${clashedName} at ${startTime}.`, 'Do you wish to cancel that booking?'];
    }
    return ['This clashes with a number of other items.', 'You will need to cancel those before you can book this one.'];
  }
  if (isOnlyOneClashedEvent) {
    const [{ name: clashedName, startTime }] = clashedEventsForTheSelectedEvent;
    return [`This overlaps with booking ${clashedName} at ${startTime}.`, 'Are you sure you wish to book this one?'];
  }
  return ['This clashes with a number of other items.'];
};

const onPositiveClashedDialogButtonClick = (
  preventOverlapMandatory,
  setClashedEvents,
  setOverlapsDialogData,
  clashedEventsForTheSelectedEvent,
  needToRemoveTheOtherClashedEvent,
  removeClashedEvent,
  setChecked,
  checked,
  isAllowedToCheckMorePoolSlots,
  isAllowedToReplaceCheckedSlot,
  setGuestSelectorDialogData,
  guestNumber,
  { relatedSlotId },
) => {
  setClashedEvents([]);
  setOverlapsDialogData({ show: false, relatedSlotId: null });
  if (needToRemoveTheOtherClashedEvent && clashedEventsForTheSelectedEvent.length === 1) {
    removeClashedEvent(clashedEventsForTheSelectedEvent[0]);
    if ((isAllowedToCheckMorePoolSlots || isAllowedToReplaceCheckedSlot) && guestNumber > 1) setGuestSelectorDialogData({ show: true, relatedSlotId });
    return;
  }
  if (preventOverlapMandatory) setChecked(checked.slice(0, -1));
};

const onNegativeClashedDialogButtonClick = (
  setClashedEvents,
  setOverlapsDialogData,
  setChecked,
  checked,
  preventOverlapMandatory,
  clashedEventsForTheSelectedEvent,
) => {
  const isClashedEvents = clashedEventsForTheSelectedEvent.length > 0;
  const isMandatoryOverlapAndClashed = preventOverlapMandatory && isClashedEvents;
  const isNotMandatoryOverlapButClashed = !preventOverlapMandatory && isClashedEvents;
  setClashedEvents([]);
  if (isMandatoryOverlapAndClashed || isNotMandatoryOverlapButClashed) setChecked(checked.slice(0, -1));
  setOverlapsDialogData({ show: false, relatedSlotId: null });
};

const getMappedBookedSlots = (bookedEvents, bookedSlotsOfThisEventWithoutPast) => bookedEvents
  .filter(({ id: timeSlotId }) => bookedSlotsOfThisEventWithoutPast.includes(parseInt(timeSlotId, 10)))
  .map(({ id: timeSlotId, numOfGuests: guestNumber = 1 }) => ({ slotId: parseInt(timeSlotId, 10), guestNumber }));

const getIsBookButtonDisabled = (checked,
  bookedSlotsOfThisEventWithoutPast) => (checked.length === 0 || (areAllCheckedSlotsAlreadyBooked(bookedSlotsOfThisEventWithoutPast, checked)));

const onBookButtonClick = ({
  saveAddon,
  checked,
  isOnlyOnePoolSlotAllowed,
  refreshTimeInterval,
  lastConfigCheckDateTime,
  setShowReloadIsNecessaryDialog,
}) => {
  const isReloadNecessary = checkIsConfigNeedsToBeRefreshed(lastConfigCheckDateTime, refreshTimeInterval);
  if (isReloadNecessary) {
    setShowReloadIsNecessaryDialog(true);
    return;
  }
  saveAddon(checked, isOnlyOnePoolSlotAllowed && checked.length === 1);
};

const getShowSwimmingExplanation = (isAllowedToBookMorePoolSlots, options, checked) => !isAllowedToBookMorePoolSlots && options.length > checked.length;

const getBookedAndCheckedSlots = (events, checked) => getArrayWithUniqueElements(
  [...events.filter(({ name }) => isSplash(name)).map(({ id }) => ({ slotId: parseInt(id, 10), guestNumber: 1 })), ...checked],
);

const SwimmingOptions = ({
  title = 'Setup your experience',
  separatorLabel = 'CLOSED FOR POOL CHANGEOVER',
  options = [],
  saveAddon = () => { },
  removeAddon = () => { },
  removeClashedEvent = () => { },
  bookedSlotsOfThisEventWithoutPast = [],
  isAllowedToBookMorePoolSlots = false,
  bookedEvents = [],
  bookedEventsWithPastEvents = [],
  quantityToBook = 1,
  maxAllowedPoolSlots,
  arrivalDate,
  maxAllowedPoolSlotsOnSameDay,
  numOfGuests = 1,
  poolRestrictionTime,
  overlapCheckPoolItems,
  overlapAllowedMinutes,
  preventOverlapMandatory,
  setNumberOfGuestsForSwimming,
  numberOfGuestsForSwimming,
  refreshTimeInterval,
  lastConfigCheckDateTime,
  numOfPreBreakMaxAllowedPoolSlots,
  isAfterDailyOpeningTime,
  backstopDate,
}) => {
  const [checked, setChecked] = useState([]);
  const [clashedEventsForTheSelectedEvent, setClashedEvents] = useState([]);
  const [poolDialogData, setPoolDialogData] = useState({ show: false, relatedSlotId: null });
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [guestSelectorDialogData, setGuestSelectorDialogData] = useState({ show: false, relatedSlotId: null });
  const [overlapsDialogData, setOverlapsDialogData] = useState({ show: false, relatedSlotId: null });
  const [showReloadIsNecessaryDialog, setShowReloadIsNecessaryDialog] = useState(false);
  const optionsLength = options.length;
  const isOnlyOnePoolSlotAllowed = getIsOnlyOnePoolSlotAllowed(arrivalDate, maxAllowedPoolSlots, maxAllowedPoolSlotsOnSameDay, poolRestrictionTime);
  const allBookedAndCheckedPoolSlots = getBookedAndCheckedSlots(bookedEventsWithPastEvents, checked);
  const bookedSlotsOnThisDay = bookedEventsWithPastEvents.filter(({ startDate }) => moment(startDate).isSame(moment(), 'days'));
  const allBookedAndCheckedPoolSlotsOnThisDay = getBookedAndCheckedSlots(bookedSlotsOnThisDay, checked);
  const bookedSlotsOfThisEventStringWithoutPast = bookedSlotsOfThisEventWithoutPast.sort().join();
  const needToRemoveTheOtherClashedEvent = preventOverlapMandatory && clashedEventsForTheSelectedEvent.length === 1;

  const isAllowedToCheckMorePoolSlots = getIsAllowedToCheckMorePoolSlots(
    arrivalDate,
    isAllowedToBookMorePoolSlots,
    maxAllowedPoolSlots,
    maxAllowedPoolSlotsOnSameDay,
    allBookedAndCheckedPoolSlots,
    checked,
    poolRestrictionTime,
    numOfPreBreakMaxAllowedPoolSlots,
  );
  const isAllowedToReplaceCheckedSlot = isOnlyOnePoolSlotAllowed && !isAllowedToCheckMorePoolSlots && checked.length === 1;

  const poolDialogMessage = getPoolDialogMessage(isOnlyOnePoolSlotAllowed, arrivalDate, clashedEventsForTheSelectedEvent, poolRestrictionTime);
  const poolDialogDate = getRemovableEntertainmentDateLabel(bookedEvents, 'splash', '', isOnlyOnePoolSlotAllowed);
  const showSwimmingExplanation = getShowSwimmingExplanation(isAllowedToBookMorePoolSlots, options, checked);

  useEffect(() => {
    const mappedBookedSlots = getMappedBookedSlots(bookedEvents, bookedSlotsOfThisEventWithoutPast);
    setChecked(prevChecked => (prevChecked.length === 0
      ? mappedBookedSlots
      : getArrayWithUniqueElements([...mappedBookedSlots, ...prevChecked])));
  }, [bookedSlotsOfThisEventStringWithoutPast]); // eslint-disable-line

  return (
    <>
      {showSwimmingExplanation && (
        <div className="explanation-message">
          <WarningMessage message="You have booked all your permitted swimming slots" />
        </div>
      )}
      {showReloadIsNecessaryDialog && (
        <Dialog
          messages={[`You've been inactive on the page for more than ${refreshTimeInterval} mins, your page will refreshed`]}
          positiveButtonText="OK"
          onPositiveButtonClick={redirectUserToSchedulePageWithReload}
          hideNegativeButton
        />
      )}
      {
        guestSelectorDialogData.show && (
          <NewDialog
            onPositiveButtonClick={() => onPositiveSavePoolDialogButtonClick(
              setChecked,
              checked,
              setGuestSelectorDialogData,
              setClashedEvents,
              guestSelectorDialogData,
              isAllowedToBookMorePoolSlots,
              numberOfGuestsForSwimming,
            )}
            onNegativeButtonClick={() => onNegativeSavePoolDialogButtonClick(setGuestSelectorDialogData, setClashedEvents)}
            negativeButtonText="CANCEL"
            positiveButtonText="OK"
            onBackArrowClick={() => onNegativeSavePoolDialogButtonClick(setGuestSelectorDialogData, setClashedEvents)}
          >
            <GuestSelector
              maxNumOfGuests={numOfGuests}
              isClashedEvent={clashedEventsForTheSelectedEvent.length > 0}
              setNumberOfGuests={setNumberOfGuestsForSwimming}
              selectedNumberOfGuests={numberOfGuestsForSwimming}
            />
          </NewDialog>
        )
      }
      {
        poolDialogData.show && (
          <NewDialog
            positiveButtonText="OK"
            negativeButtonText="CANCEL"
            onPositiveButtonClick={() => onPositivePoolDialogButtonClick(
              isOnlyOnePoolSlotAllowed,
              setPoolDialogData,
              setChecked,
              checked,
              setClashedEvents,
              setGuestSelectorDialogData,
              poolDialogData,
            )}
            onNegativeButtonClick={() => onNegativePoolDialogButtonClick(setChecked, setPoolDialogData, checked, isOnlyOnePoolSlotAllowed, setClashedEvents)}
            hideNegativeButton={!isOnlyOnePoolSlotAllowed}
            onBackArrowClick={() => onNegativePoolDialogButtonClick(setChecked, setPoolDialogData, checked, isOnlyOnePoolSlotAllowed, setClashedEvents)}
          >
            <DialogMessage messages={poolDialogMessage} date={poolDialogDate} />
          </NewDialog>
        )
      }
      {
        showCancelDialog && (
          <CancelModal
            options={options}
            checked={checked}
            onCancel={(code) => {
              setShowCancelDialog(false);
              if (bookedSlotsOfThisEventWithoutPast.includes(code)) removeAddon(code);
              if (checked.filter(({ slotId }) => slotId === code).length > 0) setChecked(checked.filter(({ slotId }) => slotId !== code));
            }}
            onNone={() => setShowCancelDialog(false)}
          />
        )
      }
      {
        overlapsDialogData.show && (
          <Dialog
            messages={getClashedDialogMessage(clashedEventsForTheSelectedEvent, preventOverlapMandatory)}
            positiveButtonText={needToRemoveTheOtherClashedEvent ? 'YES' : 'OK'}
            negativeButtonText={needToRemoveTheOtherClashedEvent ? 'NO' : 'CANCEL'}
            onPositiveButtonClick={() => onPositiveClashedDialogButtonClick(
              preventOverlapMandatory,
              setClashedEvents,
              setOverlapsDialogData,
              clashedEventsForTheSelectedEvent,
              needToRemoveTheOtherClashedEvent,
              removeClashedEvent,
              setChecked,
              checked,
              isAllowedToCheckMorePoolSlots,
              isAllowedToReplaceCheckedSlot,
              setGuestSelectorDialogData,
              numOfGuests,
              overlapsDialogData,
            )}
            onNegativeButtonClick={() => onNegativeClashedDialogButtonClick(
              setClashedEvents,
              setOverlapsDialogData,
              setChecked,
              checked,
              preventOverlapMandatory,
              clashedEventsForTheSelectedEvent,
            )}
            hideNegativeButton={clashedEventsForTheSelectedEvent.length !== 1}
          />
        )
      }
      <div className="options">
        <div className="title-container">
          <div className="options-title">{title}</div>
        </div>
        <div className="options-container">
          {!!options && options.filter(option => filterSlotsByBackstop(option, backstopDate)).map((option, index) => {
            const { code, remainingCapacity = 1, singleDate, duration = 0 } = option;
            const isChecked = getIsChecked(checked, code);
            const areSpacesAvailable = remainingCapacity !== 0 && quantityToBook <= remainingCapacity;
            const isAlreadyBooked = bookedSlotsOfThisEventWithoutPast.includes(code);

            const clashedEvents = getClashedBookedEntertainments(
              singleDate,
              duration,
              code,
              bookedSlotsOnThisDay,
              overlapCheckPoolItems,
              overlapAllowedMinutes,
            );
            const isClashedEvent = clashedEvents.length > 0;
            const shouldDisableSwimming = getShouldDisableSwimming(
              arrivalDate,
              poolRestrictionTime,
              singleDate,
              allBookedAndCheckedPoolSlots,
              numOfPreBreakMaxAllowedPoolSlots,
              isAfterDailyOpeningTime,
              maxAllowedPoolSlotsOnSameDay,
              code,
              checked,
              allBookedAndCheckedPoolSlotsOnThisDay,
            );
            const disabled = getOptionDisabled(
              isChecked,
              areSpacesAvailable,
              singleDate,
              duration,
              isAlreadyBooked,
              shouldDisableSwimming,
            );
            return (
              <div key={index}>
                <SwimmingOption
                  key={index}
                  label={getDateLabel(option)}
                  checked={isChecked}
                  color={getSlotColor(index)}
                  isClashedEvent={isClashedEvent && preventOverlapMandatory}
                  disabled={disabled}
                  onOptionClick={() => onOptionClick(
                    setChecked,
                    disabled,
                    code,
                    setPoolDialogData,
                    checked,
                    isOnlyOnePoolSlotAllowed,
                    setGuestSelectorDialogData,
                    numOfGuests,
                    clashedEvents,
                    setClashedEvents,
                    setOverlapsDialogData,
                    preventOverlapMandatory,
                    isAllowedToBookMorePoolSlots,
                    isAllowedToReplaceCheckedSlot,
                  )}
                  areSpacesAvailable={areSpacesAvailable}
                  isAlreadyBooked={isAlreadyBooked}
                />
                {index < optionsLength - 1 && <Separator label={separatorLabel} />}
              </div>
            );
          })}
        </div>
        <div className="book-time-btn">
          <Button
            text="Book time"
            disabled={getIsBookButtonDisabled(checked, bookedSlotsOfThisEventWithoutPast)}
            fontSize={14}
            onClick={() => onBookButtonClick({
              saveAddon,
              checked,
              isOnlyOnePoolSlotAllowed,
              refreshTimeInterval,
              lastConfigCheckDateTime,
              setShowReloadIsNecessaryDialog,
            })}
          />
        </div>
      </div>
      {!IsSelectedEventExpired(bookedSlotsOfThisEventWithoutPast, options) && (
        <div
          className="cancel-booking-container"
          onClick={() => handleCancel(
            setChecked,
            removeAddon,
            bookedSlotsOfThisEventWithoutPast,
            setShowCancelDialog,
            checked,
          )}
        >
          {checked.length > 0 && (
            <div className="cancel-booking">Cancel booking</div>
          )}
        </div>
      )}
    </>
  );
};

SwimmingOptions.propTypes = {
  title: PropTypes.string,
  separatorLabel: PropTypes.string,
  options: PropTypes.array,
  saveAddon: PropTypes.func,
  removeAddon: PropTypes.func,
  removeClashedEvent: PropTypes.func,
  bookedSlotsOfThisEventWithoutPast: PropTypes.array,
  isAllowedToBookMorePoolSlots: PropTypes.bool,
  bookedEvents: PropTypes.array,
  bookedEventsWithPastEvents: PropTypes.array,
  quantityToBook: PropTypes.number,
  maxAllowedPoolSlots: PropTypes.number,
  arrivalDate: PropTypes.string,
  maxAllowedPoolSlotsOnSameDay: PropTypes.number,
  numOfGuests: PropTypes.number,
  poolRestrictionTime: PropTypes.string,
  overlapCheckPoolItems: PropTypes.bool,
  overlapAllowedMinutes: PropTypes.number,
  preventOverlapMandatory: PropTypes.bool,
  setNumberOfGuestsForSwimming: PropTypes.func,
  numberOfGuestsForSwimming: PropTypes.number,
  refreshTimeInterval: PropTypes.number,
  lastConfigCheckDateTime: PropTypes.string,
  numOfPreBreakMaxAllowedPoolSlots: PropTypes.number,
  isAfterDailyOpeningTime: PropTypes.bool,
  backstopDate: PropTypes.string,
};

export default SwimmingOptions;
